/**
 * Utility functions for handling user roles safely
 * Handles both Laravel numeric roles and Next.js string roles
 */

export const getRoleName = (role) => {
  // If role is null, undefined, or empty, default to 'user'
  if (!role && role !== 0 && role !== '0') {
    return 'user';
  }

  // Check if it's a numeric role (number or numeric string)
  const roleMap = {
    0: 'admin',
    '0': 'admin',
    1: 'clab',
    '1': 'clab',
    2: 'recruiter',
    '2': 'recruiter',
    3: 'employer',
    '3': 'employer'
  };

  // If it's in the role map, use the mapped value
  if (roleMap.hasOwnProperty(role)) {
    return roleMap[role];
  }

  // If it's already a string role name, return lowercase
  if (typeof role === 'string') {
    const lowerRole = role.toLowerCase().trim();
    // Check if it's a known string role
    if (['admin', 'clab', 'recruiter', 'employer', 'master_admin', 'clab_user'].includes(lowerRole)) {
      return lowerRole;
    }
  }

  return 'user';
};

export const getUserDashboardRoute = (role) => {
  const roleName = getRoleName(role);
  
  switch (roleName) {
    case 'admin':
    case 'master_admin':
      return '/master-admin-dashboard';
    
    case 'clab':
    case 'clab_user':
    case '1':
      return '/clab-dashboard';
    
    case 'recruiter':
    case '2':
      return '/recruiter-dashboard';
    
    case 'employer':
    case '3':
      return '/employer-biodata-selection';
    
    default:
      return '/clab-dashboard'; // Default fallback
  }
};

export const hasPermission = (userRole, requiredRole) => {
  const roleName = getRoleName(userRole);
  const required = getRoleName(requiredRole);

  return roleName === required || roleName === 'admin';
};

/**
 * Role code stored by the API, keyed by the name the forms use.
 * Keep this in step with lib/roles.ts on the API side.
 */
export const ROLE_CODES = {
  admin: '0',
  clab: '1',
  recruiter: '2',
  employer: '3',
};

/** Roles a master admin may assign, in the order the forms list them. */
export const ASSIGNABLE_ROLE_OPTIONS = [
  { value: 'admin', label: 'Master Admin', description: 'Full system access and management' },
  { value: 'clab', label: 'CLAB User', description: 'Biodata review and approval' },
  { value: 'recruiter', label: 'Recruiter', description: 'Biodata submission and management' },
];

/**
 * Convert whatever a form holds — a name like 'clab' or a stored code like
 * '1' — into the code the API expects. Falls back to CLAB User.
 */
export const toRoleCode = (value) => {
  const raw = String(value ?? '').toLowerCase();
  if (ROLE_CODES[raw]) return ROLE_CODES[raw];
  if (Object.values(ROLE_CODES).includes(raw)) return raw;
  return ROLE_CODES.clab;
};

/** Find the select option matching a stored role, by name or by code. */
export const findRoleOption = (role) =>
  ASSIGNABLE_ROLE_OPTIONS.find(option => option.value === role) ||
  ASSIGNABLE_ROLE_OPTIONS.find(option => ROLE_CODES[option.value] === String(role)) ||
  null;